// audio/audio-events.js: M5 Change #9.
//
// Declarative event → sample mapping. The one place this is defined, used by:
//   • audio/wire-engine.js to subscribe each event to engine.play()
//   • audio/index.js's SAMPLES manifest cross-checks against this table
//     in dev (no missing-id drift between manifest + dispatch)
//   • tests that assert "every event has a sample" without grepping
//
// The shape stays declarative so M5 #10..#15 can append rows without
// touching wire-engine.js. Per-sample play options (volume / playbackRate
// scaling) live next to the mapping, so a designer adjusting a single
// sample's level doesn't have to chase three files.
//
// Bower overlay (#9 AC #4): bower_played is its own event registered
// here. The trick-collection layer (#10) fires it IN ADDITION TO
// trick_won_* when the winning card is a bower; wiring is a separate
// subscription so silencing one doesn't silence the other.

import { EVENTS } from './events.js';

/**
 * @typedef {{
 *   event:    string,
 *   sample:   string,
 *   playOpts: { volume?: number, playbackRate?: number },
 *   note?:    string,
 * }} AudioMapEntry
 */

/** @type {ReadonlyArray<AudioMapEntry>} */
export const AUDIO_MAP = Object.freeze([
  // Per-card landing (M5 #4, kept here so #9 + #4 share a table).
  { event: 'card_snap',                  sample: 'card_snap',      playOpts: {} },
  // Dealing animation tick (#9 emit site lands with the deal detector).
  { event: 'card_dealt',                 sample: 'card_dealt',     playOpts: { volume: 0.7 } },
  // Hover micro-tick (#9 emit site deferred, see plan carry-list).
  { event: 'card_hover',                 sample: 'card_hover',     playOpts: { volume: 0.3 } },
  // Trump-called chord stinger.
  { event: EVENTS.BID_SUBMITTED,         sample: 'trump_called',   playOpts: {} },
  // Trick won / lost, perspective baked into the event at emit time.
  { event: EVENTS.TRICK_WON_YOURS,       sample: 'trick_won_us',   playOpts: {} },
  { event: EVENTS.TRICK_WON_OPPONENTS,   sample: 'trick_won_them', playOpts: {} },
  // Bower played overlay, fires alongside trick_won_* in #10.
  { event: 'bower_played',               sample: 'bower_played',   playOpts: { volume: 0.8 } },
  // Illegal-play denial (M5 #6 wired the emit site).
  { event: 'illegal_play',               sample: 'illegal_play',   playOpts: { volume: 0.6 } },
  // Hand outcomes (delta-based, +1 / +2 / loner +4).
  { event: EVENTS.HAND_WON_PLUS1,        sample: 'hand_won_1',     playOpts: {} },
  { event: EVENTS.HAND_WON_PLUS2,        sample: 'hand_won_2',     playOpts: {} },
  { event: EVENTS.LONER_SWEEP,           sample: 'loner_sweep',    playOpts: {} },
  // Euchre outcomes, distinct sample per perspective.
  { event: EVENTS.EUCHRED,               sample: 'euchred_us',     playOpts: {} },
  { event: EVENTS.EUCHRED_THEM,          sample: 'euchred_them',   playOpts: {} },
  // Score-digit tick (#11). Pitch is per-emit (1.0 + position * 0.15);
  // see wire-engine.js for the payload merge.
  { event: 'score_tick',                 sample: 'score_tick',     playOpts: { volume: 0.5 } },
]);

/**
 * Sample ids that AUDIO_MAP references. Used by audio/index.js to
 * cross-check the SAMPLES manifest, every dispatched sample must be
 * registered, every registered sample should appear here (except future
 * #11 score_tick which gets its own dispatch path).
 *
 * @returns {Set<string>}
 */
export function expectedSampleIds() {
  const ids = new Set();
  for (const e of AUDIO_MAP) ids.add(e.sample);
  return ids;
}
